import React, {useEffect, useState, useCallback} from "react";
import {observer} from 'mobx-react-lite';
import {useService} from "../../../infrastructure/service/service";
import {Download, Upload} from "@mui/icons-material";
import {useDropzone} from "react-dropzone";
import {toast} from "react-toastify";
import { County } from "../../../entity/model/county";
import { State } from "../../../entity/model/state";
import { DocumentMetadata } from "../../../entity/model/document-metadata";
import { MarketplaceExcelRequest } from "../../../entity/dto/marketplace-excel-request-dto";
import { MarketplaceExcelData } from "../../../entity/dto/marketplace-excel-data-dto";
import {
    TableRow,
    Table,
    TableHead,
    TableBody,
    TableCell,
    Paper,
    Typography,
    Button,
    TableContainer,
    CircularProgress
} from "@mui/material";
import Box from "@mui/material/Box";
import LoadingComponent from "../components/widget/LoadingComponent";
import TableSortLabel from "@mui/material/TableSortLabel";
import TablePagination from "../components/widget/TablePagination";

import '../../../assets/css/style.css';
import '../../../assets/css/pages/marketplace-management.css';
import 'react-loading-skeleton/dist/skeleton.css';

const MarketplaceManagement = (): JSX.Element => {
    const {metadataService, stateService, countyService} = useService();
    const [loading, setLoading] = useState<boolean>(true);
    const [counties, setCounties] = useState<County[]>([]);
    const [states, setStates] = useState<State[]>([]);
    const [metadata, setMetadata] = useState<DocumentMetadata[]>([]);
    const [uploadedData, setUploadedData] = useState<MarketplaceExcelData[]>([]);
    const [submitLoading, setSubmitLoading] = useState<boolean>(false);
    const [downloadLoading, setDownloadLoading] = useState<boolean>(false);
    const [isDataValid, setIsDataValid] = useState<boolean>(true);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [totalPages, setTotalPages] = useState<number>(0);

    const tableHeaders = ['Document Name', 'State', 'County', 'Book Type', 'Index Type', 'Date Range', 'Index Book', 'Book', 'Tags'];
    const pageSize = 100;

    useEffect(() => {
        const init = async (): Promise<void> => {
            setLoading(true);

            const allStates: State[] = await stateService.getStates();
            const allCounties: County[] = await countyService.getCounties();
            
            setStates(allStates);
            setCounties(allCounties);
        }

        init().then(() => setLoading(false));
    }, []);

    const validateUploadedData = (data: MarketplaceExcelData[]): boolean => {
        if (!data || data.length === 0) return true;

        for (const row of data) {
            const validState = states.find(s => s.state === row.state?.trim());
            if (!validState) {
                toast.error(`Invalid state: "${row.state}" in row ${data.indexOf(row) + 2}`);
                return false;
            }

            const validCounty = counties.find(c => c.stateID === validState.id && c.name === row.county?.trim());
            if (!validCounty) {
                toast.error(`Invalid county: "${row.county}" for state "${row.state}" in row ${data.indexOf(row) + 2}`);
                return false;
            }
        }

        return true;
    };

    const onDrop = useCallback(async (acceptedFiles: File[]): Promise<void> => {
        const excelFiles = acceptedFiles.filter((file: File): boolean =>
            file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
            file.type === 'application/vnd.ms-excel' ||
            file.name.endsWith('.xlsx') ||
            file.name.endsWith('.xls')
        );

        if (excelFiles.length === 0)
            return;

        if (excelFiles.length > 1)
            return;

        const file = excelFiles[0];
        const data = await metadataService.validateAndUploadExcel(file, metadata);
        setUploadedData(data);

        const totalPages = Math.ceil(data.length / pageSize);
        setTotalPages(totalPages);
        setCurrentPage(1);

        const isValid = validateUploadedData(data);
        setIsDataValid(isValid);

        if (isValid && data.length > 0) {
            toast.success('Data validated successfully! You can now submit.');
        }
    }, [metadataService, metadata, states, counties, validateUploadedData]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: { 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'], 'application/vnd.ms-excel': ['.xls']},
        multiple: false}
    );

    const handleDownloadFile = async (): Promise<void> => {
        setDownloadLoading(true);
        
        const metaData: DocumentMetadata[] = await metadataService.getPendingMetadataList({body: {}});
        setMetadata(metaData);

        const countyNames = counties.map((county: County) => county.name);
        const stateNames = states.map((state: State) => state.state);

        const request: MarketplaceExcelRequest = {
            metadata: metaData,
            states: stateNames,
            counties: countyNames,
            stateObjects: states,
            countyObjects: counties
        };

        await metadataService.generateMetadataExcel(request);
        setDownloadLoading(false);
    }

    const handleUploadFile = async (): Promise<void> => {
        if (uploadedData.length === 0) return;
        if (states.length === 0) return;

        setSubmitLoading(true);
        const success = await metadataService.submitDocumentMetadata(uploadedData, states, countyService);
        if (success) {
            setUploadedData([]);
            setCurrentPage(1);
            setTotalPages(0);
        }
        setSubmitLoading(false);
    }

    const handlePaginationChange = (event: React.ChangeEvent<unknown>, activePage: number): void => {
        setCurrentPage(activePage);
    }

    const getCurrentPageData = (): MarketplaceExcelData[] => {
        const startIndex = (currentPage - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        return uploadedData.slice(startIndex, endIndex);
    }

    const renderTableRow = (row: MarketplaceExcelData, index: number): JSX.Element => {
        return (
            <TableRow key={index} className={'table-cell'}>
                <TableCell className={'no-wrap table-cell-text'} align={'left'}>
                    {row.documentName}
                </TableCell>
                <TableCell className={'no-wrap table-cell-text'} align={'left'}>
                    {row.state}
                </TableCell>
                <TableCell className={'no-wrap table-cell-text'} align={'left'}>
                    {row.county}
                </TableCell>
                <TableCell className={'no-wrap table-cell-text'} align={'left'}>
                    {row.bookType}
                </TableCell>
                <TableCell className={'no-wrap table-cell-text'} align={'left'}>
                    {row.indexType}
                </TableCell>
                <TableCell className={'no-wrap table-cell-text'} align={'left'}>
                    {row.dateRange}
                </TableCell>
                <TableCell className={'no-wrap table-cell-text'} align={'left'}>
                    {row.indexBook}
                </TableCell>
                <TableCell className={'no-wrap table-cell-text'} align={'left'}>
                    {row.book}
                </TableCell>
                <TableCell className={'no-wrap table-cell-text'} align={'left'}>
                    {row.tags}
                </TableCell>
            </TableRow>
        );
    }

    const renderEmptyRow = (): JSX.Element => {
        return (
            <TableRow>
                <TableCell colSpan={9} align='center' className='marketplace-empty-cell'>
                    <Typography id={'text-marketplace-empty'} className='empty-message'>
                        No marketplace data available. Upload an Excel file to see data.
                    </Typography>
                </TableCell>
            </TableRow>
        );
    }

    return (
        <>
            {
                loading
                    ? <LoadingComponent/>
                    : <Box className={'body-box marketplace-management'}>
                        <Paper className={'table-container'} elevation={0}>
                            <Box className={'toolbar-container'}>
                                <Button
                                    id={'btn-marketplace-download'}
                                    disabled={downloadLoading}
                                    className={`zitles-button ${downloadLoading ? 'disable' : ''}`}
                                    variant="contained"
                                    startIcon={downloadLoading ? <CircularProgress size={14} color='inherit' /> : <Download />}
                                    onClick={handleDownloadFile}>
                                    {
                                        downloadLoading
                                            ? 'Generating...'
                                            : 'Download Template'
                                    }
                                </Button>
                                <Box className='marketplace-toolbar-spacing' />
                                <Box {...getRootProps()}>
                                    <input {...getInputProps()} />
                                    <Button
                                        id={'btn-marketplace-upload'}
                                        className={'zitles-button'}
                                        variant="contained"
                                        startIcon={<Upload />}>
                                        {
                                            isDragActive
                                                ? 'Drop Excel file here'
                                                : 'Upload Template'
                                        }
                                    </Button>
                                </Box>
                            </Box>
                            <TableContainer className={'zitles-table'} component={Paper}>
                                <Table>
                                    <TableHead>
                                        <TableRow className={'table-header'}>
                                            {
                                                tableHeaders.map((header: string): JSX.Element => (
                                                    <TableCell key={header} className={'no-wrap'} align='left' padding='normal'>
                                                        <TableSortLabel
                                                            id={`marketplace-column-${header}`}
                                                            className={'table-header-text'}
                                                            active={true}
                                                            direction='desc'>
                                                            {header}
                                                        </TableSortLabel>
                                                    </TableCell>))
                                            }
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {
                                            uploadedData.length > 0
                                                ? getCurrentPageData().map(renderTableRow)
                                                : renderEmptyRow()
                                        }
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                        {
                            uploadedData.length > 0 && totalPages > 1 &&
                                <TablePagination
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    onChange={handlePaginationChange}/>
                        }
                        {
                            uploadedData.length > 0 &&
                                <Box className='marketplace-submit-container'>
                                    <Button
                                        id={'btn-submit-metadata'}
                                        disabled={submitLoading || !isDataValid}
                                        className={`zitles-button submit ${submitLoading || !isDataValid ? 'disable' : ''}`}
                                        variant="contained"
                                        onClick={handleUploadFile}
                                        startIcon={submitLoading ? <CircularProgress size={14} color='inherit' /> : null}>
                                        {
                                            submitLoading
                                                ? 'Submitting'
                                                : 'Submit'
                                        }
                                    </Button>
                                </Box>
                        }
                    </Box>
            }
        </>
    );
}

export default observer(MarketplaceManagement);
